import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Plus } from 'lucide-react';
import type { Loan } from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { EmptyState, ErrorState, Money, SearchInput, Spinner, ToneBadge } from '@/components/ui';
import { formatDate } from '@/lib/format';
import { LoanFormModal } from './LoanFormModal';
import { LoanTrackModal } from './LoanTrackModal';
import {
  LENDER_KIND_LABEL,
  LOAN_KIND_LABEL,
  LOAN_STATUS,
  REPAYMENT_MODE_LABEL,
  ratePercent,
} from './loanLabels';

type LoanRow = Loan & { bankAccount?: { label: string; bankName: string; last4: string } };
type GroupBy = 'bank' | 'kind' | 'status' | 'none';

const SORTS = [
  { value: 'nextDue', label: 'Next EMI date' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'emi', label: 'EMI amount' },
  { value: 'rate', label: 'Interest rate' },
  { value: 'lender', label: 'Lender' },
] as const;

/**
 * Settings → Payment Accounts → Loans: every loan the company has taken, each
 * linked to the bank account its EMIs leave from — grouped, sorted and
 * filtered here, and opened on its full track.
 */
export function LoansSection() {
  const { companyId, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const openId = params.get('loan');
  const [creating, setCreating] = useState(false);

  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>('bank');
  const [sort, setSort] = useState<(typeof SORTS)[number]['value']>('nextDue');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [repaymentMode, setRepaymentMode] = useState('');
  const [due, setDue] = useState('');

  const query = {
    companyId,
    pageSize: 200,
    sort,
    direction,
    ...(search ? { q: search } : {}),
    ...(status ? { status } : {}),
    ...(kind ? { kind } : {}),
    ...(bankAccountId ? { bankAccountId } : {}),
    ...(repaymentMode ? { repaymentMode } : {}),
    ...(due === 'overdue' ? { overdue: 'true' } : due ? { dueWithinDays: due } : {}),
  };
  const { data, isLoading, error } = useQuery({
    queryKey: ['loans', query],
    queryFn: () => api.loans.list(query),
  });
  const { data: accounts } = useQuery({
    queryKey: ['settings-bank-accounts', companyId, 'options'],
    queryFn: () => api.settings.bankAccounts({ companyId, pageSize: 200 }),
  });

  const loans = useMemo(() => (data?.items ?? []) as LoanRow[], [data]);
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: 'All loans', items: loans }];
    const label = (loan: LoanRow) =>
      groupBy === 'bank'
        ? loan.bankAccount
          ? `${loan.bankAccount.label} (…${loan.bankAccount.last4})`
          : 'No bank account'
        : groupBy === 'kind'
          ? (LOAN_KIND_LABEL[loan.kind] ?? loan.kind)
          : (LOAN_STATUS[loan.status]?.label ?? loan.status);
    const map = new Map<string, LoanRow[]>();
    for (const loan of loans) map.set(label(loan), [...(map.get(label(loan)) ?? []), loan]);
    return [...map.entries()].map(([key, items]) => ({ key, label: key, items }));
  }, [loans, groupBy]);

  const open = (id: string) =>
    setParams((current) => ({ ...Object.fromEntries(current), loan: id }));
  const close = () =>
    setParams((current) => {
      const next = Object.fromEntries(current);
      delete next.loan;
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900">Loans</h2>
          <p className="text-sm text-ink-500">
            Loans the company has taken, each linked to the bank account its EMIs leave from.
          </p>
        </div>
        {can('loan:create') ? (
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" aria-hidden /> Add loan
          </button>
        ) : null}
      </div>

      <div className="card space-y-3 p-3">
        <SearchInput
          placeholder="Search lender, loan reference or account no."
          label="Search loans"
          onSearch={setSearch}
        />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <label className="block">
            <span className="label">Group by</span>
            <select
              aria-label="Group by"
              className="input"
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as GroupBy)}
            >
              <option value="bank">Bank account</option>
              <option value="kind">Loan type</option>
              <option value="status">Status</option>
              <option value="none">No grouping</option>
            </select>
          </label>
          <label className="block">
            <span className="label">Sort by</span>
            <div className="flex gap-1">
              <select
                aria-label="Sort by"
                className="input min-w-0"
                value={sort}
                onChange={(event) => setSort(event.target.value as typeof sort)}
              >
                {SORTS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary shrink-0 px-2"
                aria-label={
                  direction === 'asc'
                    ? 'Ascending — switch to descending'
                    : 'Descending — switch to ascending'
                }
                onClick={() => setDirection(direction === 'asc' ? 'desc' : 'asc')}
              >
                {direction === 'asc' ? (
                  <ArrowUpNarrowWide className="h-4 w-4" aria-hidden />
                ) : (
                  <ArrowDownWideNarrow className="h-4 w-4" aria-hidden />
                )}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="label">Status</span>
            <select
              aria-label="Status"
              className="input"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">Draft, active, closed</option>
              {Object.entries(LOAN_STATUS).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Loan type</span>
            <select
              aria-label="Loan type"
              className="input"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
            >
              <option value="">All types</option>
              {Object.entries(LOAN_KIND_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Bank account</span>
            <select
              aria-label="Bank account"
              className="input"
              value={bankAccountId}
              onChange={(event) => setBankAccountId(event.target.value)}
            >
              <option value="">All accounts</option>
              {(accounts?.items ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.label} (…{account.accountNumber.slice(-4)})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">Repayment</span>
            <select
              aria-label="Repayment"
              className="input"
              value={repaymentMode}
              onChange={(event) => setRepaymentMode(event.target.value)}
            >
              <option value="">Any</option>
              {Object.entries(REPAYMENT_MODE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="label">EMI due</span>
            <select
              aria-label="EMI due"
              className="input"
              value={due}
              onChange={(event) => setDue(event.target.value)}
            >
              <option value="">Any time</option>
              <option value="7">In the next 7 days</option>
              <option value="30">In the next 30 days</option>
              <option value="overdue">Overdue</option>
            </select>
          </label>
        </div>
      </div>

      {isLoading ? (
        <Spinner />
      ) : error ? (
        <ErrorState error={error} />
      ) : !loans.length ? (
        <EmptyState
          illustration="wallet"
          title="No loans here"
          hint="Add a loan the company has taken to track its EMIs."
        />
      ) : (
        groups.map((group) => (
          <section key={group.key} aria-label={group.label} className="card overflow-hidden">
            <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-ink-100 bg-ink-50 px-4 py-2">
              <h3 className="text-sm font-semibold text-ink-800">{group.label}</h3>
              <span className="text-xs text-ink-500">
                {group.items.length} {group.items.length === 1 ? 'loan' : 'loans'} · outstanding{' '}
                <Money
                  minor={group.items.reduce(
                    (sum, loan) => sum + (loan.summary?.outstanding ?? 0),
                    0,
                  )}
                />
              </span>
            </header>
            <ul className="divide-y divide-ink-100">
              {group.items.map((loan) => (
                <li key={loan.id}>
                  <button
                    type="button"
                    className="grid w-full gap-2 px-4 py-3 text-left hover:bg-brand-50/40 sm:grid-cols-[1.4fr_1.2fr_1fr_1.1fr_auto] sm:items-center"
                    onClick={() => open(loan.id)}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-ink-900">
                        {loan.lender.name}
                      </span>
                      <span className="block text-xs text-ink-500">
                        {LENDER_KIND_LABEL[loan.lender.kind]} · {LOAN_KIND_LABEL[loan.kind]} ·{' '}
                        {ratePercent(loan.rateBasisPoints)}
                      </span>
                    </span>
                    <span className="min-w-0 text-sm">
                      <span className="block font-mono text-xs text-ink-500">{loan.reference}</span>
                      <span className="block truncate text-ink-700">
                        A/c {loan.loanAccountNumber}
                      </span>
                    </span>
                    <span className="text-sm">
                      <span className="block text-xs text-ink-500">Outstanding</span>
                      <Money minor={loan.summary?.outstanding} className="font-semibold" />
                    </span>
                    <span className="text-sm">
                      <span className="block text-xs text-ink-500">
                        Next EMI · {REPAYMENT_MODE_LABEL[loan.repaymentMode]}
                      </span>
                      {loan.summary?.nextDue ? (
                        <>
                          <Money minor={loan.summary.nextDue.amount} /> on{' '}
                          {formatDate(loan.summary.nextDue.dueDate)}
                        </>
                      ) : (
                        <span className="text-ink-500">—</span>
                      )}
                    </span>
                    <span className="flex flex-wrap gap-1 sm:justify-end">
                      <ToneBadge tone={LOAN_STATUS[loan.status]?.tone ?? 'neutral'}>
                        {LOAN_STATUS[loan.status]?.label ?? loan.status}
                      </ToneBadge>
                      {loan.summary?.overdueCount ? (
                        <ToneBadge tone="negative">{loan.summary.overdueCount} overdue</ToneBadge>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}

      {creating ? (
        <LoanFormModal
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            setCreating(false);
            open(id);
          }}
        />
      ) : null}
      {openId ? <LoanTrackModal loanId={openId} onClose={close} /> : null}
    </div>
  );
}
